import React, { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { ArrowRight } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { API_URL } from '../utils/api';

interface IndustryItem {
  id: string;
  name: string;
  slug: string;
  icon: string;
  gradient: string;
  description?: string;
}

interface IndustryExpertiseData {
  badgeText: string;
  badgeIcon: string;
  title: string;
  subtitle: string;
  industries: IndustryItem[];
  ctaText: string;
  visible: boolean;
}

// ============================================
// SKELETON
// ============================================

const IndustryExpertiseSkeleton = () => (
  <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-white via-gray-50 to-white overflow-hidden">
    <div className="max-w-6xl mx-auto space-y-12">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <div className="h-8 w-40 bg-gray-200 rounded-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>
        <div className="h-8 bg-gray-200 rounded w-64 mx-auto relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
        <div className="space-y-2 max-w-xl mx-auto">
          <div className="h-4 bg-gray-200 rounded w-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
          <div className="h-4 bg-gray-200 rounded w-4/5 mx-auto relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>
      </div>

      {/* Industry cards grid — 4 cols matching default data */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-white/80 rounded-2xl p-6 sm:p-8 shadow-lg border border-white/60 space-y-4 text-center">
            {/* Icon */}
            <div className="w-14 h-14 bg-gray-200 rounded-xl mx-auto relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            {/* Name */}
            <div className="h-5 bg-gray-200 rounded w-3/4 mx-auto relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            {/* CTA link */}
            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ============================================
// COMPONENT
// ============================================

export function IndustryExpertiseSection() {
  const [industryData, setIndustryData] = useState<IndustryExpertiseData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIndustryData();
  }, []);

  const fetchIndustryData = async () => {
    try {
      const timestamp = new Date().getTime();
      const response = await fetch(`${API_URL}/homepage/industry-expertise?t=${timestamp}`, {
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store',
      });

      if (!response.ok) {
        setDefaultData();
        return;
      }

      const result = await response.json();
      if (result.success && result.data) {
        setIndustryData(result.data);
      } else {
        setDefaultData();
      }
    } catch (error: any) {
      setDefaultData();
    } finally {
      setLoading(false);
    }
  };

  const setDefaultData = () => {
    setIndustryData({
      badgeText: 'Industry Expertise',
      badgeIcon: 'Award',
      title: 'Specialized by Industry',
      subtitle: 'We understand the unique challenges, compliance requirements, and technical nuances of your industry.',
      industries: [
        { id: 'ind-1', name: 'Automotive', slug: 'automotive', icon: 'Boxes', gradient: 'from-[#D74444] to-[#e85e5e]', description: 'View Examples' },
        { id: 'ind-2', name: 'Manufacturing', slug: 'manufacturing', icon: 'Cog', gradient: 'from-[#E87D3E] to-[#E8B84D]', description: 'View Examples' },
        { id: 'ind-3', name: 'Energy', slug: 'energy', icon: 'BarChart3', gradient: 'from-[#2F2E5C] to-[#4a487a]', description: 'View Examples' },
        { id: 'ind-4', name: 'Healthcare', slug: 'healthcare', icon: 'Shield', gradient: 'from-[#D74444] to-[#e85e5e]', description: 'View Examples' }
      ],
      ctaText: 'View Examples',
      visible: true
    });
  };

  const getIconComponent = (iconName: string) => {
    const IconComponent = (LucideIcons as any)[iconName];
    return IconComponent || LucideIcons.Circle;
  };

  // Show skeleton while loading
  if (loading) {
    return <IndustryExpertiseSkeleton />;
  }

  // Don't render if not visible or no data
  if (!industryData?.visible) return null;

  const BadgeIcon = getIconComponent(industryData.badgeIcon);
  const industryCount = industryData.industries.length;

  const getGridCols = () => {
    if (industryCount <= 2) return 'grid-cols-1 sm:grid-cols-2';
    if (industryCount === 3) return 'grid-cols-2 sm:grid-cols-3';
    return 'grid-cols-2 sm:grid-cols-2 lg:grid-cols-4';
  };

  return (
    <section
      id="industries"
      className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-white via-gray-50 to-white overflow-hidden"
    >
      {/* Premium Matte Background with Gradient Overlays */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-[#D74444] via-[#e85e5e] to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-[#E87D3E] via-[#E8B84D] to-transparent rounded-full blur-3xl" />
      </div>

      {/* Subtle Matte Texture Overlay */}
      <div className="absolute inset-0 opacity-[0.01]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")'}} />

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-xl px-4 py-2 rounded-full mb-4 border border-white/40 shadow-lg">
            <BadgeIcon className="w-4 h-4 text-[#E87D3E]" />
            <span className="text-sm font-semibold bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">
              {industryData.badgeText}
            </span>
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 text-[#2F2E5C]">
            {industryData.title}
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {industryData.subtitle}
          </p>
        </div>

        <div className={`grid ${getGridCols()} gap-5 sm:gap-6`}>
          {industryData.industries.map((industry) => {
            const IndustryIcon = getIconComponent(industry.icon);
            return (
              <Link key={industry.id} to={`/industries/${industry.slug}`}>
                <div className="group relative bg-white/80 backdrop-blur-sm rounded-2xl p-6 sm:p-8 shadow-lg border border-white/60 hover:shadow-2xl hover:scale-105 transition-all duration-300 text-center overflow-hidden">
                  <div className={`absolute inset-0 bg-gradient-to-br ${industry.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                  <div className="absolute inset-0 opacity-[0.02]" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, #2F2E5C 1px, transparent 0)', backgroundSize: '30px 30px'}} />

                  <div className="relative z-10">
                    <div className={`relative w-14 h-14 bg-gradient-to-br ${industry.gradient} rounded-xl flex items-center justify-center mx-auto mb-4 text-white shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                      <div className={`absolute inset-0 bg-gradient-to-br ${industry.gradient} blur-md opacity-50 group-hover:opacity-75 transition-opacity`} />
                      <div className="relative z-10">
                        <IndustryIcon className="w-6 h-6" />
                      </div>
                    </div>

                    <h3 className="font-bold text-[#2F2E5C] mb-4 group-hover:bg-gradient-to-r group-hover:from-[#2F2E5C] group-hover:to-[#4a487a] group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">
                      {industry.name}
                    </h3>

                    <div className={`inline-flex items-center font-bold gap-2 text-sm group-hover:gap-3 transition-all duration-300 bg-gradient-to-r ${industry.gradient} bg-clip-text text-transparent`}>
                      {industry.description || industryData.ctaText}
                      <ArrowRight className="w-4 h-4 text-[#D74444] group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}